import React from 'react';
import {
  useAutoRollCountdown,
  useDesignViewport,
  useResumeCountdown,
  useRoll,
} from '@root/hooks';
import { FINISH_POSITION } from '@root/lib';
import { balanceSelector } from '@root/store/slices/balanceSlice';
import { fundingSelector, isRunPaused } from '@root/store/slices/fundingSlice';
import { journeySelector } from '@root/store/slices/journeySlice';
import { roundSelector } from '@root/store/slices/roundSlice';
import { useActions, useTypedSelector } from '@root/store/store';
import { useImageUrl } from '@shared/components/ImageContext';
import { formatMoney } from '@shared/lib';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from './ControlsBlock.module.scss';

// Design calls out the last 3 seconds.
const URGENT_SECONDS = 3;

type Box = { x: number; y: number; width: number; height: number };

// Design lines sit in a 1.3 box, so height gives the font size.
const fontFor = (height: number): number => Math.round(height / 1.3);

// Design coords, straight from the PaidReady frames.
const LAYOUT = {
  landscape: {
    balance: { x: 805, y: 678, width: 340, height: 77 },
    controls: { x: 765, y: 504, width: 420, height: 125 },
    event: { x: 765, y: 213, width: 420, height: 71 },
    countdown: { x: 0, y: 0, width: 420, height: 32 },
    roll: { x: 0, y: 40, width: 420, height: 56 },
    hint: { x: 0, y: 104, width: 420, height: 21 },
    totals: { x: 735, y: 790, width: 475, height: 73 },
    cell: { y: 12, width: 145, height: 49, xs: [12, 165, 318] },
    label: 16,
    value: 29,
  },
  portrait: {
    balance: { x: 70, y: 80, width: 250, height: 57 },
    controls: { x: 140, y: 627, width: 234, height: 109 },
    event: { x: 16, y: 746, width: 358, height: 28 },
    countdown: { x: 0, y: 0, width: 234, height: 21 },
    roll: { x: 0, y: 29, width: 234, height: 56 },
    hint: { x: 0, y: 93, width: 234, height: 16 },
    totals: { x: 16, y: 784, width: 349, height: 44 },
    cell: { y: 4, width: 103, height: 36, xs: [12, 123, 234] },
    label: 13,
    value: 21,
  },
} as const;

function box(b: Box): React.CSSProperties {
  return {
    left: b.x,
    top: b.y,
    width: b.width,
    height: b.height,
  };
}

const ControlsBlock: React.FC = () => {
  const { t } = useTranslation();
  const { round } = useTypedSelector(roundSelector);
  const { rollInFlight, introPlayed } = useTypedSelector(journeySelector);
  const { balance } = useTypedSelector(balanceSelector);
  const { roll, canRoll, isFree, rollAmount, hasRun } = useRoll();
  const paused = useTypedSelector(isRunPaused);
  const { resumeDeadline } = useTypedSelector(fundingSelector);
  const { showFundingDialog } = useActions();
  const resumeLeft = useResumeCountdown(resumeDeadline);
  const viewport = useDesignViewport();
  const { img, imgProps } = useImageUrl();

  const state = round?.state;
  const position = state?.position ?? 0;
  const seconds = useAutoRollCountdown(
    state?.rollDeadline,
    hasRun && canRoll && !rollInFlight && introPlayed
  );
  // A paused run keeps the board, so the controls carry the deadline.
  const resumeWithin = `${String(resumeLeft.minutes).padStart(2, '0')}:${String(
    resumeLeft.seconds
  ).padStart(2, '0')}`;

  // Before a run exists the start screen owns the controls.
  if (!hasRun) return null;

  const L = viewport.portrait ? LAYOUT.portrait : LAYOUT.landscape;
  const urgent = seconds > 0 && seconds <= URGENT_SECONDS;
  const symbol = balance.currencySymbol;

  const countdownStyle = {
    ...box(L.countdown),
    fontSize: fontFor(L.countdown.height),
  };
  const hintStyle = { ...box(L.hint), fontSize: fontFor(L.hint.height) };

  const landed = !rollInFlight ? state?.currentRoll : null;
  const tile = landed
    ? state?.board?.find((entry) => entry.position === landed.position)
    : null;
  const event =
    landed?.outcome === 'FREE_ROLL'
      ? {
          headline: t('Your next roll is free!'),
          detail: t('1 free-roll credit ready to use.'),
        }
      : landed?.outcome === 'MULTIPLIER'
        ? {
            headline: t('{{multiplier}}X collected · +{{amount}}', {
              multiplier: tile?.multiplier ?? '',
              amount: formatMoney(symbol, landed.payout),
            }),
            detail: t('Added to your balance. Keep playing!'),
          }
        : null;

  const preparing = !introPlayed;
  const countdown = isFree
    ? t('Free auto-roll in {{seconds}}s', { seconds })
    : t('Auto-roll in {{seconds}}s', { seconds });

  const totals = [
    { label: t('SPENT'), value: formatMoney(symbol, state?.totalWagered ?? 0) },
    {
      label: t('COLLECTED'),
      value: formatMoney(symbol, state?.totalPaid ?? 0),
    },
    { label: t('POSITION'), value: `${position} / ${FINISH_POSITION}` },
  ];

  return (
    <div
      className={styles.layer}
      style={{
        width: viewport.width,
        height: viewport.height,
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})`,
      }}
    >
      <div
        className={styles.balance}
        style={{
          ...box(L.balance),
          backgroundImage: `url(${img('WalletFrame')})`,
        }}
      >
        <img className={styles.wallet} {...imgProps('WalletIcon')} alt="" />
        <span className={styles.balanceValue}>
          {formatMoney(symbol, balance.amount)}
        </span>
      </div>

      <div className={styles.controls} style={box(L.controls)}>
        {/* Server rolls anyway, watched or not. */}
        {preparing ? (
          <p className={styles.countdown} style={countdownStyle}>
            {t('Preparing the board…')}
          </p>
        ) : paused ? (
          <p className={styles.countdown} style={countdownStyle}>
            {t('Resume within {{left}}', { left: resumeWithin })}
          </p>
        ) : (
          seconds > 0 &&
          !rollInFlight && (
            <p
              className={cn(styles.countdown, {
                [styles.countdown_urgent]: urgent,
              })}
              style={countdownStyle}
            >
              {countdown}
            </p>
          )
        )}
        <button
          type="button"
          className={styles.roll}
          style={
            {
              ...box(L.roll),
              '--button-skin': `url(${img('ButtonSkin_Primary')})`,
            } as React.CSSProperties
          }
          disabled={!paused && (preparing || !canRoll)}
          onClick={() => (paused ? showFundingDialog() : void roll())}
        >
          {paused && t('OPEN PAUSED ROUND')}
          {!paused && preparing && t('PREPARING BOARD')}
          {!paused && !preparing && isFree && t('FREE ROLL')}
          {!paused &&
            !preparing &&
            !isFree &&
            `${t('ROLL')} · ${formatMoney(symbol, rollAmount)}`}
        </button>
        <p className={styles.hint} style={hintStyle}>
          {paused && t('Add funds and resume before expiry.')}
          {!paused &&
            isFree &&
            t('Rewards still use {{amount}} per roll.', {
              amount: formatMoney(symbol, rollAmount),
            })}
          {!paused && !isFree && t('Exact roll needed to finish.')}
        </p>
      </div>

      {event && (
        <div
          className={styles.event}
          style={
            {
              ...box(L.event),
              '--notice-skin': `url(${img('NoticeFrame')})`,
            } as React.CSSProperties
          }
        >
          <span className={styles.eventHeadline}>{event.headline}</span>
          {!viewport.portrait && (
            <span className={styles.eventDetail}>{event.detail}</span>
          )}
        </div>
      )}

      <div className={styles.totals} style={box(L.totals)}>
        {totals.map((item, i) => (
          <div
            key={item.label}
            className={styles.cell}
            style={{
              left: L.cell.xs[i],
              top: L.cell.y,
              width: L.cell.width,
              height: L.cell.height,
            }}
          >
            <span
              className={styles.cellLabel}
              style={{ fontSize: fontFor(L.label) }}
            >
              {item.label}
            </span>
            <span
              className={styles.cellValue}
              style={{ fontSize: fontFor(L.value) }}
            >
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ControlsBlock;
